import { Track } from "@/src/lib/tracking";
/**
 * XPayReportCard数据转换工具
 * 将Cards API数据转换为XPayReportData格式
 * 处理token、price和score数据
 */

import {
  XPayReportData,
  XPayScoreData,
  PriceChangeStatus,
} from "../XPayReportTypes";
import { ChatCategoryCardType } from "../type";
import { formatPriceForDisplay } from "../../../PriceDisplay/utils";
// import Translations from "@translate/components/Chat/Cards/utils/xPayDataTransform";
// import { I18nUtil } from "@utils/i18n";
import { Cards } from "@features/api/ChatApi";
import { getStockPriceData } from "@/src/hooks/useTokenPopoverData/getStockPriceData";
import {
  calculateChangeStatus,
  formatPercentage,
} from "@hooks/useTokenPopoverData/utils";
import { getRegionCurrencySymbol } from "@utils/stock";

// const I18n = I18nUtil.createScoped(Translations);

/**
 * 从Cards数据中提取价格信息
 */
const extractPriceData = (cards: ChatCategoryCardType) => {
  const properties = cards.properties;
  const marketData = properties?.market_data;
  const tradingData = properties?.trading_data;
  const isCrypto = cards.type === "token" || cards.type === "pool";
  const currency = cards.properties.currency
    ? getRegionCurrencySymbol(cards.properties.currency)
    : "";
  if (!marketData && !tradingData) {
    return {
      current: isCrypto
        ? {
            integerPart: "",
            leftDigits: null,
            zeroCount: 0,
            isSuccess: false,
          }
        : "",
      change: {
        amount: "",
        percentage: "",
        status: "flat" as PriceChangeStatus,
      },
    };
  }
  // 使用 getStockPriceData 处理股票价格数据
  const stockPriceData = getStockPriceData({
    marketData: tradingData,
    currentPrice: tradingData?.current_price?.toString(),
  });
  // 获取第最后一个市场数据项，如果为空则提供默认值
  const lastMarketData = stockPriceData.marketDataList[
    stockPriceData.marketDataList.length - 1
  ] || {
    current_price: tradingData?.current_price || 0,
    marketLabel: "",
    price_change_percentage: 0,
  };

  const currentPrice = marketData?.current_price || "";
  const priceChangeArray = marketData?.price_change_percentage || [];

  // 找到 24h 的价格变化数据
  const change24h = priceChangeArray.find(
    (item: any) => item.time_period === "24h",
  );

  // let label = isCrypto
  //   ? I18n.t("last 24hrs")
  //   : lastMarketData?.marketLabel?.replace("：", ":").split(":")?.[0];
  // if (currency !== "$") {
  //   label = "";
  // }

  const priceChange24hValue = Number(change24h?.percentage) || 0;
  return {
    current: isCrypto
      ? formatPriceForDisplay(currentPrice as string)
      : lastMarketData?.current_price?.toString(),
    change: {
      // 原始逻辑
      // percentage: formatPercentage(
      //   isCrypto
      //     ? priceChange24hValue
      //     : lastMarketData?.price_change_percentage,
      // ),
      // TODO 2026.1.6 在新增港股的迭代中由于后端设计变更，trading_data中不再返回时间相关字段，导致无法判断盘口状态，所以也无法正确计算涨跌幅，所以股票类型涨跌幅临时设置为空
      percentage: isCrypto ? formatPercentage(priceChange24hValue) : "",
      status: isCrypto ? calculateChangeStatus(priceChange24hValue) : "flat",
    },
    label: "",
    currency,
  };
};

/**
 * 从Cards数据中提取TokenInfo
 */
const extractTokenInfo = (cards: Cards) => {
  const properties = cards.properties as any;
  let name = properties?.name;
  if (properties?.market === "HK" && properties?.short_name) {
    name = `${properties?.short_name} (${properties?.symbol})`;
  }
  return {
    name: name || "Unknown Token",
    symbol: properties?.ticker || properties?.symbol,
    avatarSrc: properties?.icon || properties.logo,
    id: properties.id,
    type: cards.type,
  };
};

/**
 * 从Cards数据中提取评分数据
 */
const extractScoreData = (cards: Cards): XPayScoreData | undefined => {
  const properties = cards.properties as any;
  const level = properties?.level;

  if (!level) {
    return undefined;
  }

  return {
    score: level.final || level.overall || "A+",
    level: level.final || level.overall,
    breakdown: {
      fundamental: level.fundamental,
      tokenomics: level.tokenomics,
      momentum: level.momentum,
      competition: level.competition,
      growth: level.growth,
      valuation: level.valuation,
    },
  };
};

/**
 * 主要转换函数：将Cards数据转换为XPayReportData
 */
export const transformCardsToXPayData = (
  cards: Cards,
): XPayReportData | undefined => {
  if (!cards || !cards.properties) {
    return undefined;
  }

  try {
    const token = extractTokenInfo(cards);
    const price = extractPriceData(cards as ChatCategoryCardType);
    const score = extractScoreData(cards);
    // 生成 forecastUrl
    let type = cards.type as any;
    const isHKStock = price.currency !== "$";
    const properties = cards.properties as any;
    let tokenIdentifier = "";

    if (type === "pool" || type === "token") {
      tokenIdentifier = properties?.id || "";
      if (type === "token") {
        type = "crypto";
      }
    } else if (type === "stock") {
      tokenIdentifier = properties?.symbol || "";
    }

    let forecastUrl =
      type && tokenIdentifier
        ? `/${type}/${tokenIdentifier}/forecast`
        : undefined;

    if (isHKStock) {
      forecastUrl = `/${type}/${tokenIdentifier}`;
    }

    const result = {
      token,
      price,
      score,
      forecastUrl,
    };
    return result;
  } catch (error) {
    Track.error(error, { tag: "xPayDataTransform", msg: "转换失败:" });
    return undefined;
  }
};
